import React, { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "@fuse/hooks";
import FuseUtils from "@fuse/utils/FuseUtils";
import AppBar from "@mui/material/AppBar";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import FormControlLabel from "@mui/material/FormControlLabel";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import moment from "moment";
import OAuthService from "src/app/auth/services/oAuthService";
import oAuthServiceConfig from "src/app/auth/services/oAuthService/oAuthServiceConfig";
import * as Actions from "./store/actions";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import customStyles from "app/shared-components/SelectCustomStyle";
import * as Flags from "country-flag-icons/react/3x2";
import { useState } from "react";
import { toast } from "react-toastify";
import { Stack, Tooltip, bottomNavigationClasses } from "@mui/material";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon/FuseSvgIcon";
import CustomDatePicker from "../utilities/CustomDatePicker";

const defaultFormState = {
  id: FuseUtils.generateGUID(),
  title: "",
  start: "",
  end: "",
  desc: "",
  country: "",
};

function EventDialog(props) {
  const dispatch = useDispatch();
  const eventDialog = useSelector(
    ({ calendarApp }) => calendarApp.events.eventDialog
  );
  const { form, handleChange, setForm, setInForm } = useForm(defaultFormState);
  const start = moment(form.start, "yyyy-MM-dd");
  const [selectedCountry, setSelectedCountry] = useState({});
  const [selectedTitle, setSelectedTitle] = useState({});
  const [btnDisabled, setBtnDisabled] = useState(true);
  const [duplicateError, setDuplicateError] = useState(false);
  const [customEventValue, setCustomEventValue] = useState('')
  const [type, setType] = useState(eventDialog.type);

  const { countryOptions, currentEvents, titleOptions } = props;

  useEffect(() => {
    if (props.selectedCountry) {
      setSelectedCountry(props.selectedCountry);
    } else if (countryOptions.length) {
      setSelectedCountry(
        countryOptions.find((item) => item.value == "United Arab Emirates")
      );
    }
  }, [countryOptions, props.selectedCountry]);

  const changeHandler = (data) => {
    setSelectedCountry(data);
    if (
      currentEvents
        .map((ce) => ce.country?.toLowerCase())
        .includes(data.label?.toLowerCase())
    ) {
      setDuplicateError(true);
      setBtnDisabled(true);
    } else {
      setDuplicateError(false);
      setBtnDisabled(false);
    }
  };

  useEffect(() => {
    if (eventDialog.props.open) {
      setType(eventDialog.type);
      setForm({ ...eventDialog.data });

      if (eventDialog.type === "new") {
        setForm({
          ...defaultFormState,
          ...eventDialog.data,
          id: FuseUtils.generateGUID(),
        });
        setSelectedTitle({});
      }
    }
  }, [eventDialog.props.open]);

  const handleTitleChange = (data) => {
    setSelectedTitle(data);
    if (
      currentEvents
        .map((ce) => ce.title?.toLowerCase())
        .includes(data.label?.toLowerCase())
    ) {
      setDuplicateError(true);
      setBtnDisabled(true);
    } else {
      setDuplicateError(false);
      setBtnDisabled(false);
    }
    if (data.label === 'Custom Event') {
      setBtnDisabled(true);
    }
  };

  const initDialog = useCallback(() => {
    /**
     * Dialog type: 'edit'
     */
    if (type === "edit" && eventDialog.data) {
      const labels = titleOptions.map(to => to.label?.toLowerCase())
      const label = eventDialog.data.title?.toLowerCase();
      if (!labels.includes(label)) {
        setSelectedTitle({
          value: 'Custom Event',
          label: 'Custom Event'
        });
        setCustomEventValue(eventDialog.data.title)
      }
      else
        setSelectedTitle({
          value: eventDialog.data.title,
          label: eventDialog.data.title,
        });
    }
  }, [eventDialog.data, type]);

  useEffect(() => {
    /**
     * After Dialog Open
     */
    if (eventDialog.props.open) {
      initDialog();
    }
  }, [eventDialog.props.open, initDialog]);

  function closeComposeDialog() {
    return type === "edit"
      ? dispatch(Actions.closeEditEventDialog())
      : dispatch(Actions.closeNewEventDialog());
  }

  function handleSubmit(event) {
    event.preventDefault();
    const payload = {
      id: form.id,
      holidayStartDate: moment(form.start).format("yyyy-MM-DD"),
      holidayEndDate: moment(form.end).format("yyyy-MM-DD"),
      description: form.desc,
      title: selectedTitle.label === 'Custom Event' ? customEventValue : selectedTitle.label,
      country: selectedCountry.code,
    };
    if (type === "new") {
      dispatch(Actions.addEvent(payload)).then(props.getCalendarEvent);
      toast.success("Holiday Added");
    } else {
      dispatch(Actions.updateEvent(payload)).then(props.getCalendarEvent);
      toast.success("Holiday Updated");
    }
    closeComposeDialog();
  }

  const url = `${process.env.REACT_APP_HR_API_KEY_URL}${oAuthServiceConfig.holidays}`;

  const handleRemove = async (id) => {
    if (form.id) {
      try {
        await OAuthService.deleteData(url, form.id);
        props.getCalendarEvent();
        closeComposeDialog();
        toast.success("Holiday Deleted");
      } catch (error) {
        setError(error.message);
      }
    }
  };

  const handleClose = () => {
    closeComposeDialog();
  };



  return (
    <Dialog
      {...eventDialog.props}
      onClose={closeComposeDialog}
      fullWidth
      maxWidth="xs"
      component="form"
    >
      <AppBar position="static">
        <Toolbar className="flex w-full justify-between">
          <Typography variant="subtitle1" color="inherit">
            {type === "new" ? "Add Holiday/Event" : "Update Holiday/Event"}
          </Typography>
          <IconButton onClick={handleClose}>
            <Icon>close</Icon>
          </IconButton>
        </Toolbar>
      </AppBar>

      <form>
        <DialogContent classes={{ root: "p-16 pb-0 sm:p-24 sm:pb-0" }}>
          <label>*Holiday/Event Type</label>
          <Select
            name="title"
            placeholder="Holiday/Event Type"
            value={selectedTitle}
            onChange={handleTitleChange}
            className="select mt-8 mb-16"
            classNamePrefix="select"
            options={titleOptions.filter(
              (to) =>
                !currentEvents
                  .map((ce) => ce.title?.toLowerCase())
                  .includes(to.value?.toLowerCase())
            )}
            styles={customStyles()}
          />
          {selectedTitle.value === 'Custom Event' && <><label>*Event</label>
            <TextField
              className="mt-8 mb-16"
              label="Event"
              type="text"
              name="event"
              value={customEventValue}
              onChange={e => {
                const { value } = e.target;
                setCustomEventValue(value)
                setBtnDisabled(value === '')
                if (currentEvents.map(ce => ce.title?.toLowerCase()).includes(value?.toLowerCase())) {
                  setBtnDisabled(true);
                  setDuplicateError(true);
                } else {
                  setBtnDisabled(false);
                  setDuplicateError(false);
                }
              }}
              variant="outlined"
              fullWidth
            /></>}
          {duplicateError && <p>The event is already created.</p>}
          <Stack>
            <label>*Start</label>
            <CustomDatePicker
              format={'dd-MM-yyyy'}
              value={form.start}
              minDate={type === 'new' ? new Date() : undefined}
              maxDate={form.end || undefined}
              handleChange={(date) => {
                setInForm("start", date)
                setBtnDisabled(date === '')
                if(currentEvents.map(ce => ce.start).includes(date)) {
                  setBtnDisabled(true)
                } else {
                  setBtnDisabled(false);
                }
              }}
            />
          </Stack>
          <Stack>

            <label>*End</label>
            <CustomDatePicker
              format={'dd-MM-yyyy'}
              value={form.end}
              minDate={form.start || undefined}
              handleChange={(date) => {
                setInForm("end", date)
                setBtnDisabled(date === '')
                if(currentEvents.map(ce => ce.end).includes(date)) {
                  setBtnDisabled(true)
                } else {
                  setBtnDisabled(false);
                }
              }}
            />
          </Stack>
          <label>*Country</label>
          {!!countryOptions.length && (
            <Select
              name="country"
              placeholder="Select Country"
              //value={(countryOptions && countryName) && countryOptions.filter(obj => countryName.includes(obj.value))}
              value={selectedCountry}
              onChange={changeHandler}
              className="select mt-8 mb-16"
              classNamePrefix="select"
              options={countryOptions}
              getOptionLabel={(e) => (
                <div style={{ display: "flex", alignItems: "center" }}>
                  {e.icon}
                  <span style={{ marginLeft: 5 }}>{e.label}</span>
                </div>
              )}
              styles={customStyles()}
            />
          )}
          <label>Description</label>
          <TextField
            className="mt-8 mb-16"
            id="desc"
            label="Description"
            type="text"
            name="desc"
            value={form.desc}
            onChange={handleChange}
            multiline
            rows={5}
            variant="outlined"
            fullWidth
          />
        </DialogContent>

        {type === "new" ? (
          <DialogActions className="justify-between px-8 sm:px-16">
            <div></div>
            <Tooltip title={"Save Holiday/Event"}>
              <Button
                onClick={handleSubmit}
                variant="contained"
                color="primary"
                type="submit"
                disabled={btnDisabled}
              >
                <FuseSvgIcon>heroicons-solid:document</FuseSvgIcon>
              </Button>
            </Tooltip>
          </DialogActions>
        ) : (
          <DialogActions className="justify-between px-8 sm:px-16">
            <Tooltip title={"Save Holiday/Event"}>
              <Button
                onClick={handleSubmit}
                variant="contained"
                color="primary"
                type="submit"
                disabled={btnDisabled}
              >
                <FuseSvgIcon>heroicons-solid:document</FuseSvgIcon>
              </Button>
            </Tooltip>
            <Tooltip title={"Delete Holiday/Event"}>
              <Button variant="contained"
                color="warning"
                onClick={handleRemove}>
                <FuseSvgIcon>heroicons-solid:trash</FuseSvgIcon>
              </Button>
            </Tooltip>
          </DialogActions>
        )}
      </form>
    </Dialog>
  );
}

export default EventDialog;
